import {
	type FetchWalletMeBalanceNonCustodialParams,
	type GetBalanceCurrencyDTO,
	type GetBalanceResponseDTO,
	isWeb3UserMigrationDoneAtom,
	minutesToMs,
	StakingType,
	useFormatAmount,
	useFormatter,
	useSettings
} from '@capsicum/common';
import { useNonCustodialBalance } from '@socios-blk/core';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useFanTokenStakingConfig } from '../../staking/hooks/useFanTokenStakingConfig';
import { hasEnoughToken } from '../../wallet/models/util/walletHasEnoughToken';
import { type NCWBalanceType, type NCWTokenBalanceType } from '../models/NCWBalance';
import { convertNcwBalanceCurrencies, roundDownNum } from '../util';

import { useBalanceHelper } from './useBalanceHelper';

/**
 * @returns defaultBalanceAmount The total wallet balance number amount in default currency
 * (does not include default currency. e.g. 555.55)
 * @returns formattedDefaultBalance The total wallet balance formatted with default currency and locale (e.g. 555.55 CHZ)
 * @returns altBalanceAmount The total wallet balance number amount in alternate currency
 * (does not include alternate currency. e.g. 66.66)
 * @returns formattedAltBalance The total wallet balance formatted with alternate currency and locale (e.g. 66.66 EUR)
 * @returns tokenBalances The balance array of non-Chiliz tokens the user holds
 * @returns getTokenBalanceBy The callback to retrieve the balance of a specific token by predicate
 * @returns currencies The user account's currencies (e.g. default currency, alternate currency, etc )
 * @returns isPending Whether this hook is pending or not
 * @returns ncwBalance The raw API response payload
 * @returns chilizBalance The Chiliz token balance data
 * @returns allBalances The balance array of all tokens the user holds
 */
export const useNCWBalance = (
	queryOptions: Parameters<typeof useNonCustodialBalance>[1] = {},
	queryParams: FetchWalletMeBalanceNonCustodialParams = {}
): NCWBalanceType => {
	const { formatCrypto } = useFormatter();
	const isMigrationDone = useAtomValue(isWeb3UserMigrationDoneAtom);
	const { formatAmount } = useFormatAmount();
	const { areConfigsLoading, isAllowed: isStakeAllowed } = useFanTokenStakingConfig(StakingType.STAKE);
	const {
		settings: { isExternalCurrencyInWalletEnabled }
	} = useSettings();

	const params = {
		...queryParams,
		isStakable: queryParams.isStakable !== undefined ? queryParams.isStakable : isStakeAllowed
	};

	//get NCW balance raw data from socios-blk/native
	const {
		data: ncwBalance,
		isFetching: isBalanceFetching,
		isLoading: isBalanceLoading,
		refetch: refetchNcWalletBalance
	} = useNonCustodialBalance(params, {
		...queryOptions,
		enabled: !areConfigsLoading,
		refetchInterval: isMigrationDone ? minutesToMs(10) : minutesToMs(0.5)
	});

	const { altShortCode, convertToTokenBalance, currencies, defaultShortCode, isCurrencyFetching, isCurrencyLoading } =
		useBalanceHelper(ncwBalance);

	const defaultSmartContractAddress = currencies?.defaultCurrency?.smartContractAddress ?? '';

	//format total NCW blance with Default Currency and locale. e.g. 666.66 CHZ
	const defaultBalanceAmount = roundDownNum(ncwBalance?.totalChilizBalance ?? '0');
	const formattedDefaultBalance = formatCrypto(defaultBalanceAmount, defaultShortCode);

	//format total NCW blance with Alternate Currency and locale. e.g. 555.55 EUR
	const altBalanceAmount = roundDownNum(ncwBalance?.totalFiatBalance ?? 0);
	const formattedAltBalance = formatCrypto(altBalanceAmount, altShortCode);

	//TODO: to remove. Currently ncwBalance.currencies is a Object, not a Array; awaiting BE to fix currencies data type
	const currenciesArray = convertNcwBalanceCurrencies(ncwBalance?.currencies);

	const allBalances = currenciesArray.map(convertToTokenBalance);
	const chilizBalance = allBalances.find(tb => tb.shortCode === 'CHZ');
	const tokenBalances = allBalances.filter(tb => {
		if (isExternalCurrencyInWalletEnabled) {
			return tb.shortCode !== 'CHZ';
		}

		return tb.shortCode !== 'CHZ' && !tb.isExternalToken;
	});
	const getTokenBalanceBy = useCallback(
		(predicate: (b: NCWTokenBalanceType) => boolean) => allBalances?.find(predicate),
		[allBalances]
	);

	const formatCHZtoFiat = (amount: number) => {
		return `${formatAmount(Number(amount) * Number(chilizBalance?.conversionToFiat?.conversionRate) || 0, { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${chilizBalance?.conversionToFiat?.fiatShortCode}`;
	};

	const isCHZBalancePositive = (chilizBalance?.amount || 0) > 0;
	const getCurrencyBy = useCallback(
		(predicate: (b: GetBalanceCurrencyDTO) => boolean) => currenciesArray?.find(predicate),
		[currenciesArray]
	);

	const hasEnoughBalance = useCallback(
		(currencyId: string, amountRequired: number): boolean => {
			const currency = getCurrencyBy(c => c.currency.id === currencyId);
			const checkTokenAmount = hasEnoughToken(Number(currency?.totalAmount) || 0);

			return checkTokenAmount(amountRequired);
		},
		[getCurrencyBy]
	);

	const hasEnoughStaked = useCallback(
		(currencyId: string, amountRequired: number): boolean => {
			const currency = getCurrencyBy(c => c.currency.id === currencyId);
			const checkTokenAmount = hasEnoughToken(Number(currency?.stakedAmount) || 0);

			return checkTokenAmount(amountRequired);
		},
		[getCurrencyBy]
	);

	return {
		allBalances,
		altBalanceAmount,
		chilizBalance,
		currencies,
		currenciesArray,
		defaultBalanceAmount,
		defaultSmartContractAddress,
		formatCHZtoFiat,
		formattedAltBalance,
		formattedDefaultBalance,
		getCurrencyBy,
		getTokenBalanceBy,
		hasEnoughBalance,
		hasEnoughStaked,
		isCHZBalancePositive,
		isFetching: isCurrencyFetching || isBalanceFetching,
		isPending: isCurrencyLoading || isBalanceLoading,
		ncwBalance: ncwBalance as unknown as GetBalanceResponseDTO,
		refetchNcWalletBalance,
		tokenBalances
	};
};
